import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Search, Users, FileSignature, FileText, Hash, MessageSquare, ArrowRight, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

const SECTION_META = {
  clients:  { label: "Clientes",     icon: Users },
  quotes:   { label: "Cotizaciones", icon: FileSignature },
  invoices: { label: "Facturas",     icon: FileText },
  sgm:      { label: "SGM Web",      icon: Hash },
  messages: { label: "Mensajes",     icon: MessageSquare },
};

/**
 * Global Cmd+K / Ctrl+K command palette for the Admin Portal.
 * Searches across clients, quotes, invoices, sgm requests and messages
 * via /api/admin/search and navigates to the matching tab on selection.
 */
export default function CommandPalette({ open, onOpenChange, onNavigate }) {
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  // Reset state on close
  useEffect(() => {
    if (!open) {
      setQ("");
      setData(null);
      return;
    }
    // focus the input shortly after mount
    setTimeout(() => inputRef.current?.focus(), 60);
  }, [open]);

  // Debounced search
  useEffect(() => {
    if (!open) return;
    if (!q.trim()) {
      setData(null);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data: res } = await api.get(`/admin/search?q=${encodeURIComponent(q.trim())}`);
        setData(res);
      } catch {
        setData({ total: 0, sections: {} });
      } finally {
        setLoading(false);
      }
    }, 250);
  }, [q, open]);

  const handleSelect = (item) => {
    onNavigate?.(item);
    onOpenChange(false);
  };

  const sections = data?.sections || {};
  const allEmpty = data && data.total === 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#1F1F25] border-[#2E2E33] text-white max-w-2xl rounded-none p-0 overflow-hidden gap-0"
        data-testid="command-palette"
      >
        <DialogTitle className="sr-only">Buscador global</DialogTitle>
        <DialogDescription className="sr-only">
          Busque clientes, cotizaciones, facturas, solicitudes SGM Web y mensajes en todo el portal admin.
        </DialogDescription>
        <div className="flex items-center border-b border-[#2E2E33] px-4 py-3 gap-3">
          {loading ? (
            <Loader2 className="w-4 h-4 text-[#F59E0B] animate-spin shrink-0" />
          ) : (
            <Search className="w-4 h-4 text-[#F59E0B] shrink-0" />
          )}
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar clientes, cotizaciones, facturas, SGM Web, mensajes…"
            className="flex-1 bg-transparent outline-none text-white placeholder:text-[#71717A] text-sm"
            data-testid="command-palette-input"
            autoComplete="off"
          />
          <kbd className="hidden sm:inline-flex font-mono text-[10px] text-[#71717A] border border-[#2E2E33] px-1.5 py-0.5 uppercase tracking-widest">ESC</kbd>
        </div>

        <div className="max-h-[60vh] overflow-y-auto" data-testid="command-palette-results">
          {!q.trim() ? (
            <div className="px-6 py-12 text-center text-[#71717A] text-sm font-mono uppercase tracking-widest">
              // Escribe para buscar en todo el portal
            </div>
          ) : allEmpty ? (
            <div className="px-6 py-12 text-center" data-testid="command-palette-empty">
              <Search className="w-8 h-8 text-[#2E2E33] mx-auto mb-3" />
              <p className="text-[#A1A1AA] text-sm">Sin coincidencias para <span className="font-mono text-[#F59E0B]">"{q}"</span></p>
            </div>
          ) : (
            <div className="py-2">
              {Object.entries(sections).map(([key, items]) => {
                if (!items?.length) return null;
                const { label, icon: Icon } = SECTION_META[key] || { label: key, icon: ArrowRight };
                return (
                  <div key={key} className="mb-1" data-testid={`command-palette-section-${key}`}>
                    <div className="px-4 py-2 font-mono text-[10px] uppercase tracking-widest text-[#71717A] flex items-center gap-2 border-b border-[#2E2E33]/50">
                      <Icon className="w-3 h-3 text-[#F59E0B]" /> // {label}
                    </div>
                    {items.map((it) => (
                      <button
                        key={`${it.type}-${it.id}`}
                        type="button"
                        onClick={() => handleSelect(it)}
                        className="w-full text-left px-4 py-3 hover:bg-[#26262C] flex items-center gap-3 group border-l-2 border-transparent hover:border-[#F59E0B] transition-colors"
                        data-testid={`command-palette-item-${it.type}-${it.id}`}
                      >
                        <Icon className="w-4 h-4 text-[#71717A] group-hover:text-[#F59E0B] shrink-0" />
                        <div className="min-w-0 flex-1">
                          <div className="text-sm text-white truncate">{it.label}</div>
                          {it.sublabel && (
                            <div className="font-mono text-[10px] text-[#71717A] truncate uppercase tracking-widest mt-0.5">{it.sublabel}</div>
                          )}
                        </div>
                        <ArrowRight className="w-3.5 h-3.5 text-[#2E2E33] group-hover:text-[#F59E0B] shrink-0" />
                      </button>
                    ))}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="border-t border-[#2E2E33] px-4 py-2 bg-[#141418] flex items-center justify-between font-mono text-[10px] uppercase tracking-widest text-[#71717A]">
          <span>↑ ↓ navegar · ↵ abrir</span>
          <span>{data ? `${data.total} resultado${data.total === 1 ? "" : "s"}` : ""}</span>
        </div>
      </DialogContent>
    </Dialog>
  );
}
